import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Brain, 
  TrendingUp, 
  Calendar, 
  AlertTriangle, 
  CheckCircle, 
  Clock,
  Heart,
  Activity,
  Zap,
  Target,
  FileText,
  Loader2,
  RefreshCw,
  ChevronRight,
  Info
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import axios from 'axios';
import { API } from '@/App';

const PredictiveHealth = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [analysis, setAnalysis] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadLatestAnalysis();
    loadAnalysisHistory();
  }, []);

  const loadLatestAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/predictive-health/latest`);
      setAnalysis(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error loading analysis:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysisHistory = async () => {
    try {
      const response = await axios.get(`${API}/predictive-health/history?limit=5`);
      setHistory(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const generateNewAnalysis = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/predictive-health/analysis`);
      setAnalysis(response.data);
      loadAnalysisHistory(); // Refresh history
      toast({
        title: "Analysis Generated",
        description: "Your predictive health analysis has been generated successfully!"
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to generate analysis';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      case 'moderate': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getHealthScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTestTypeIcon = (testType) => {
    switch (testType?.toLowerCase()) {
      case 'blood_test': return <Activity className="w-4 h-4" />;
      case 'imaging': return <FileText className="w-4 h-4" />;
      case 'screening': return <Target className="w-4 h-4" />;
      default: return <Heart className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-6 sm:py-8 pt-24 ">
          <div className="flex items-center justify-center py-20">
            <div className="text-center max-w-md">
              <Loader2 className="w-16 h-16 animate-spin text-teal-600 mx-auto mb-6" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Loading Health Analysis</h3>
              <p className="text-gray-600 mb-4">Retrieving your personalized health data...</p>
              <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                <p className="text-sm text-teal-700">
                  💡 Tip: AI analysis may take 10-15 seconds for comprehensive results
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show generating state with detailed progress
  if (generating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-6 sm:py-8 pt-24 ">
          <div className="flex items-center justify-center py-20">
            <div className="text-center max-w-md">
              <div className="relative mb-6">
                <Loader2 className="w-20 h-20 animate-spin text-teal-600 mx-auto" />
                <Brain className="w-8 h-8 text-teal-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">AI Analysis in Progress</h3>
              <p className="text-gray-600 mb-4">Our AI is analyzing your medical reports and health data...</p>
              
              <div className="space-y-3 text-left bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-600">Reviewing medical reports</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                  <span className="text-sm text-gray-600">Analyzing health trends</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                  <span className="text-sm text-gray-600">Calculating risk assessments</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse" style={{animationDelay: '0.6s'}}></div>
                  <span className="text-sm text-gray-600">Generating recommendations</span>
                </div>
              </div>
              
              <p className="text-xs text-gray-500 mt-4">
                This may take 10-15 seconds • Results are cached for faster future access
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 sm:py-8 pt-24 ">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                <Brain className="w-8 h-8 text-teal-600" />
                {t('nav.predictiveHealth')}
              </h1>
              <p className="text-gray-600">
                {t('predictiveHealth.subtitle')}
              </p>
            </div>
            <Button
              onClick={generateNewAnalysis}
              disabled={generating}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {generating ? 'Analyzing...' : 'Generate New Analysis'}
            </Button>
          </div>
        </div>

        {!analysis ? (
          <Card className="text-center py-16">
            <CardContent>
              <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                No Predictive Analysis Available
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Generate your first AI-powered health analysis to get personalized insights, 
                risk assessments, and recommended checkups based on your medical reports.
              </p>
              <Button
                onClick={generateNewAnalysis}
                disabled={generating}
                className="bg-teal-600 hover:bg-teal-700"
                size="lg"
              >
                {generating ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Brain className="w-5 h-5 mr-2" />
                )}
                {generating ? 'Generating Analysis...' : 'Generate Health Analysis'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="risks">Risk Assessment</TabsTrigger>
              <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              {/* Health Score Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-teal-600" />
                    Overall Health Score
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className={`text-4xl font-bold ${getHealthScoreColor(analysis.overall_health_score)}`}>
                        {analysis.overall_health_score}/100
                      </div>
                      <p className="text-gray-600 mt-1">
                        Based on {analysis.reports_analyzed_count} medical reports
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Generated on</p>
                      <p className="font-semibold text-gray-700">
                        {formatDate(analysis.generated_at)}
                      </p>
                    </div>
                  </div>
                  <Progress 
                    value={analysis.overall_health_score} 
                    className="h-3 mb-4" 
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-3 bg-teal-50 rounded-lg">
                      <Calendar className="w-6 h-6 text-teal-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-700">Next Checkup</p>
                      <p className="text-xs text-teal-600">
                        {formatDate(analysis.next_checkup_date)}
                      </p>
                    </div>
                    <div className="text-center p-3 bg-cyan-50 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-cyan-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-700">Analysis Type</p>
                      <p className="text-xs text-cyan-600 capitalize">
                        {analysis.analysis_type}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Health Trends */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-cyan-600" />
                    Health Trends Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {analysis.health_trends}
                  </p>
                </CardContent>
              </Card>

              {/* Key Insights */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Key Health Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analysis.key_insights.map((insight, index) => (
                      <div key={`item-${index}`} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <p className="text-gray-700">{insight}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Action Items */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-orange-600" />
                    Immediate Action Items
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analysis.action_items.map((action, index) => (
                      <div key={`item-${index}`} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <ChevronRight className="w-4 h-4 text-orange-600" />
                        </div>
                        <p className="text-gray-700">{action}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Risk Assessment Tab */}
            <TabsContent value="risks" className="space-y-6">
              <div className="grid gap-4">
                {analysis.risk_assessments.map((risk, index) => (
                  <Card key={`item-${index}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="w-6 h-6 text-orange-500" />
                          <h3 className="text-lg font-semibold capitalize text-gray-800">
                            {risk.risk_category} Risk
                          </h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getRiskLevelColor(risk.risk_level)}>
                            {risk.risk_level}
                          </Badge>
                          <div className="text-right">
                            <p className="font-bold text-gray-800">
                              {risk.risk_score}%
                            </p>
                            <p className="text-xs text-gray-500">
                              {Math.round(risk.confidence * 100)}% confidence
                            </p>
                          </div>
                        </div>
                      </div>
                      <Progress value={risk.risk_score} className="mb-4" />
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {risk.reasoning}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Recommendations Tab */}
            <TabsContent value="recommendations" className="space-y-6">
              <div className="grid gap-4">
                {analysis.recommended_tests.map((test, index) => (
                  <Card key={`item-${index}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-start gap-3">
                          {getTestTypeIcon(test.test_type)}
                          <div>
                            <h3 className="font-semibold text-gray-800">
                              {test.test_name}
                            </h3>
                            <p className="text-sm text-gray-500 capitalize">
                              {test.test_type.replace('_', ' ')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={getPriorityColor(test.priority)}>
                            {test.priority}
                          </Badge>
                          <p className="text-sm text-gray-600 mt-1">
                            {formatDate(test.recommended_date)}
                          </p>
                          {test.frequency && (
                            <p className="text-xs text-gray-500">
                              Frequency: {test.frequency}
                            </p>
                          )}
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed mb-4">
                        {test.reasoning}
                      </p>
                      <Button size="sm" variant="outline" className="w-full">
                        <Calendar className="w-4 h-4 mr-2" />
                        Schedule Test
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="space-y-6">
              <div className="space-y-4">
                {history.length === 0 ? (
                  <Card className="text-center py-12">
                    <CardContent>
                      <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600">No previous analyses found</p>
                    </CardContent>
                  </Card>
                ) : (
                  history.map((item, index) => (
                    <Card key={`item-${index}`} className={index === 0 ? 'ring-2 ring-teal-200' : ''}>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Brain className="w-5 h-5 text-teal-600" />
                            <span className="font-semibold text-gray-800">
                              Analysis #{history.length - index}
                            </span>
                            {index === 0 && (
                              <Badge className="bg-teal-100 text-teal-800">Current</Badge>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-800">
                              Score: {item.overall_health_score}/100
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatDate(item.generated_at)}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div className="bg-red-50 p-2 rounded">
                            <p className="text-xs text-gray-600">Risk Factors</p>
                            <p className="font-semibold text-red-600">
                              {item.risk_assessments.length}
                            </p>
                          </div>
                          <div className="bg-blue-50 p-2 rounded">
                            <p className="text-xs text-gray-600">Recommended Tests</p>
                            <p className="font-semibold text-blue-600">
                              {item.recommended_tests.length}
                            </p>
                          </div>
                          <div className="bg-green-50 p-2 rounded">
                            <p className="text-xs text-gray-600">Reports Analyzed</p>
                            <p className="font-semibold text-green-600">
                              {item.reports_analyzed_count}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
      
      <Toaster />
    </div>
  );
};

export default PredictiveHealth;